/**
 * Soleia — bag customiser
 *
 * Flow: size -> body colour -> trim colour -> add to basket.
 *
 * Recolours a photograph rather than rendering a 3D model, so the preview keeps
 * the real terry texture, stitching and hardware.
 *
 * Per view three generated assets drive it:
 *   soleia-bag-<view>-shade.png     greyscale shading map; alpha = silhouette
 *   soleia-bag-<view>-mask.png      R = body, G = trim, B = hardware
 *   soleia-bag-<view>-hardware.png  real metal pixels, composited untinted
 *
 * Per zone: draw the shading map, blend the chosen colour over it with the
 * canvas `overlay` operator, then clip to that zone's mask. `overlay` branches
 * on the backdrop, so shadows stay dark and highlights stay bright. A plain
 * `multiply` — the obvious approach — flattens pale colours into mud.
 *
 * ROTATION: `frames` is an array. Named views get buttons; past
 * SPIN_MIN_FRAMES it switches to drag-to-spin on its own, because flicking
 * between a handful of fixed angles reads as broken rather than as turning.
 */
(function () {
  'use strict';

  var P = window.SoleiaColours;
  if (!P) return;

  var SPIN_MIN_FRAMES = 8;
  var UNDO_DEPTH = 20;

  // The hero and the size cards want opposite things from the size scaling.
  //
  // On the CARDS the whole point is comparing sizes, so they use the true
  // ratio and share a baseline — 25 cm should look half of 50 cm.
  //
  // On the HERO that ratio just buys dead space: a small bag bottom-aligned in
  // a fixed box leaves ~40% empty above it. So the hero remaps the range into
  // a narrow band — still a visible progression, far less emptiness — and
  // parks the leftover height mostly above the bag so it still reads grounded.
  var HERO_SCALE_MIN = 0.80;
  var CARD_SCALE_MIN = 0.55;
  var GROUND_BIAS = 0.72;

  var HARDWARE = [
    { key: 'silver', label: 'Silver', swatch: '#C9CCD1', tint: null },
    { key: 'gold',   label: 'Gold',   swatch: '#DDB86F', tint: '#C79B45' }
  ];
  /**
   * Recolour the hardware artwork to a finish, preserving luminance.
   *
   * A flat multiply turns every near-white pixel into solid tint, which made
   * the pale rim light along the bag's base render as a bright gold band.
   * A gradient map instead maps dark->dark, mid->tint, highlight->white, so
   * specular highlights stay bright and only the mid-tones take the colour.
   * Cached per view+finish; the pixel loop runs once, not every frame.
   */
  var tintCache = {};
  function tintedHardware(fr, hardware, w, h) {
    var key = fr.key + '|' + hardware.key;
    if (tintCache[key]) return tintCache[key];

    var hex = hardware.tint.replace('#', '');
    var tr = parseInt(hex.substr(0, 2), 16);
    var tg = parseInt(hex.substr(2, 2), 16);
    var tb = parseInt(hex.substr(4, 2), 16);

    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var g = c.getContext('2d');
    g.drawImage(fr.hardware, 0, 0);

    var d = g.getImageData(0, 0, w, h), p = d.data;

    // The artwork also carries the pale rail and contact shadow along the bag's
    // base. That is not hardware and must keep its own colour whatever finish
    // is chosen. It always sits below a clear band of empty rows, so find the
    // widest gap in the lower half and leave everything beneath it untouched.
    var rowHas = new Uint8Array(h), x, y;
    for (y = 0; y < h; y++) {
      var off = y * w * 4;
      for (x = 0; x < w; x++) {
        if (p[off + x * 4 + 3] > 8) { rowHas[y] = 1; break; }
      }
    }
    var gapEnd = -1, gapLen = 0;
    y = Math.floor(h * 0.5);
    while (y < h) {
      if (rowHas[y]) { y++; continue; }
      var start = y;
      while (y < h && !rowHas[y]) y++;
      if (y < h && (y - start) > gapLen) { gapLen = y - start; gapEnd = y; }
    }
    var cutoff = (gapLen >= 20 && gapEnd > -1) ? gapEnd : h;

    for (y = 0; y < cutoff; y++) {
      for (x = 0; x < w; x++) {
        var i = (y * w + x) * 4;
        if (!p[i + 3]) continue;
        var l = (0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2]) / 255;
        if (l <= 0.5) {
          var f = l * 2;
          p[i] = tr * f; p[i + 1] = tg * f; p[i + 2] = tb * f;
        } else {
          var t = (l - 0.5) * 2;
          p[i] = tr + (255 - tr) * t;
          p[i + 1] = tg + (255 - tg) * t;
          p[i + 2] = tb + (255 - tb) * t;
        }
      }
    }
    g.putImageData(d, 0, 0);
    tintCache[key] = c;
    return c;
  }

  var hardwareByKey = function (k) {
    for (var i = 0; i < HARDWARE.length; i++) if (HARDWARE[i].key === k) return HARDWARE[i];
    return HARDWARE[0];
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Could not load ' + src)); };
      img.src = src;
    });
  }

  function scratch(w, h) {
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    return c;
  }

  /** Isolate one RGB channel of `img` into a canvas where it becomes the alpha. */
  function channelToAlpha(img, channel) {
    var c = scratch(img.naturalWidth, img.naturalHeight);
    var g = c.getContext('2d');
    g.drawImage(img, 0, 0);
    var d = g.getImageData(0, 0, c.width, c.height);
    var p = d.data;
    for (var i = 0; i < p.length; i += 4) {
      var v = p[i + channel];
      p[i] = p[i + 1] = p[i + 2] = 255;
      p[i + 3] = v;
    }
    g.putImageData(d, 0, 0);
    return c;
  }

  function supportsOverlay(ctx) {
    ctx.globalCompositeOperation = 'overlay';
    var ok = ctx.globalCompositeOperation === 'overlay';
    ctx.globalCompositeOperation = 'source-over';
    return ok;
  }

  /** How many swatches sit on one row — for arrow-key navigation. */
  function columnsOf(container) {
    var kids = container.querySelectorAll('button');
    if (kids.length < 2) return 1;
    var top = kids[0].offsetTop, n = 1;
    while (n < kids.length && kids[n].offsetTop === top) n++;
    return n;
  }

  // -------------------------------------------------------------------------

  function Customiser(root) {
    this.root = root;
    this.frames = [];
    this.frame = 0;
    this.step = 1;
    this.history = [];
    this.touched = { size: false, body: false, trim: false, hardware: false };
    this.showNames = false;
    this.showWays = false;
    this.saved = false;

    this.body = P.byNumber(root.dataset.defaultBody || 5) || P.all[4];
    this.trim = P.byNumber(root.dataset.defaultTrim || 3) || P.all[2];
    this.hardware = hardwareByKey(root.dataset.defaultHardware || 'silver');

    this.sizes = this.parseSizes();
    this.size = this.sizes[Math.min(1, this.sizes.length - 1)];

    this.canvas = root.querySelector('[data-sc-canvas]');
    this.marker = root.querySelector('[data-sc-marker]');
    this.form = root.querySelector('form');

    this.readFromUrl();
    this.buildUi();
    this.start();
  }

  /**
   * Sizes come from the product's variants. Dimensions and the one-word use
   * note come from a section setting rather than metafields, because the store
   * has no Admin API token to define metafields with.
   */
  Customiser.prototype.parseSizes = function () {
    var raw;
    try { raw = JSON.parse(this.root.dataset.sizes || 'null'); } catch (e) { raw = null; }
    if (!raw || !raw.length) {
      raw = [{ code: '1', label: '30cm', price: '', variant: '' }];
    }

    var details = {};
    (this.root.dataset.sizeDetails || '').split('\n').forEach(function (line) {
      var parts = line.split('|');
      if (parts.length < 2) return;
      var key = parseInt(parts[0], 10);
      if (isNaN(key)) return;
      details[key] = { dims: (parts[1] || '').trim(), note: (parts[2] || '').trim() };
    });

    var sizes = raw.map(function (s) {
      var cm = parseInt(String(s.label).replace(/[^0-9]/g, ''), 10);
      var d = details[cm] || {};
      return {
        code: s.code, label: s.label, price: s.price, variant: s.variant,
        cm: isNaN(cm) ? null : cm, dims: d.dims || '', note: d.note || ''
      };
    });

    var cms = sizes.map(function (s) { return s.cm || 0; }).filter(Boolean);
    var max = cms.length ? Math.max.apply(null, cms) : 0;
    var min = cms.length ? Math.min.apply(null, cms) : 0;
    var span = max - min;
    sizes.forEach(function (s) {
      if (!max || !s.cm) { s.scale = 1; s.cardScale = 1; return; }
      s.cardScale = Math.max(CARD_SCALE_MIN, s.cm / max);
      // Even progression across [HERO_SCALE_MIN, 1] rather than a raw ratio,
      // so every size still looks distinct without leaving a third of the
      // hero empty at the small end.
      s.scale = span
        ? HERO_SCALE_MIN + (1 - HERO_SCALE_MIN) * ((s.cm - min) / span)
        : 1;
    });
    return sizes;
  };

  Customiser.prototype.readFromUrl = function () {
    var q = new URLSearchParams(window.location.search);
    var b = P.byNumber(q.get('body')), t = P.byNumber(q.get('trim'));
    if (b) { this.body = b; this.touched.body = true; }
    if (t) { this.trim = t; this.touched.trim = true; }
    var hw = q.get('hw');
    if (hw) this.hardware = hardwareByKey(hw);
    var s = q.get('size');
    for (var i = 0; i < this.sizes.length; i++) {
      if (this.sizes[i].code === s) { this.size = this.sizes[i]; this.touched.size = true; break; }
    }
    // Frames have not loaded yet, so hold the requested view and resolve later.
    this.wantView = q.get('view') || '';
    if (this.touched.body && this.touched.trim) this.step = 0;
  };

  Customiser.prototype.start = function () {
    var self = this;
    var f = this.root.dataset, list;
    try { list = JSON.parse(f.frames || 'null'); } catch (e) { list = null; }
    if (!list || !list.length) list = [{ shade: f.shade, mask: f.mask, hardware: f.hardware }];

    Promise.all(list.map(function (fr) {
      return Promise.all([
        loadImage(fr.shade),
        loadImage(fr.mask),
        fr.hardware ? loadImage(fr.hardware) : Promise.resolve(null)
      ]).then(function (r) {
        return {
          key: fr.key || '', label: fr.label || '',
          shade: r[0], hardware: r[2],
          body: channelToAlpha(r[1], 0),
          trim: channelToAlpha(r[1], 1),
          hw: channelToAlpha(r[1], 2)
        };
      });
    })).then(function (frames) {
      self.frames = frames;
      self.w = frames[0].shade.naturalWidth;
      self.h = frames[0].shade.naturalHeight;
      self.layer = scratch(self.w, self.h);
      self.zoneBuf = scratch(self.w, self.h);
      self.overlayOk = supportsOverlay(self.zoneBuf.getContext('2d'));
      self.root.classList.add('sc--ready');

      var spinnable = frames.length >= SPIN_MIN_FRAMES;
      self.root.classList.toggle('sc--spinnable', spinnable);
      if (spinnable) self.bindDrag();
      self.buildViews();
      self.resize();
      window.addEventListener('resize', function () { self.resize(); });
    }).catch(function (err) {
      self.root.classList.add('sc--failed');
      var note = self.root.querySelector('[data-sc-error]');
      if (note) {
        note.hidden = false;
        note.textContent = 'The live preview could not load, but you can still choose your colours and order.';
      }
      if (window.console) console.error('[soleia-customiser]', err);
    });
  };

  // --- rendering -----------------------------------------------------------

  /** Tint one zone: shading map -> overlay colour -> clip to mask. */
  Customiser.prototype.paintZone = function (dst, colour, mask) {
    var fr = this.frames[this.frame];
    var g = this.zoneBuf.getContext('2d');
    g.globalCompositeOperation = 'source-over';
    g.clearRect(0, 0, this.w, this.h);
    g.drawImage(fr.shade, 0, 0);
    g.globalCompositeOperation = this.overlayOk ? 'overlay' : 'multiply';
    g.fillStyle = colour;
    g.fillRect(0, 0, this.w, this.h);
    g.globalCompositeOperation = 'destination-in';
    g.drawImage(mask, 0, 0);
    g.globalCompositeOperation = 'source-over';
    dst.drawImage(this.zoneBuf, 0, 0);
  };

  /** Composite the whole bag once, at natural size. */
  Customiser.prototype.renderLayer = function () {
    if (!this.frames.length) return;
    var fr = this.frames[this.frame];
    var lg = this.layer.getContext('2d');
    lg.setTransform(1, 0, 0, 1, 0, 0);
    lg.clearRect(0, 0, this.w, this.h);
    this.paintZone(lg, this.body.display, fr.body);
    this.paintZone(lg, this.trim.display, fr.trim);

    if (fr.hardware) {
      if (this.hardware.tint) {
        lg.drawImage(tintedHardware(fr, this.hardware, this.w, this.h), 0, 0);
      } else {
        lg.drawImage(fr.hardware, 0, 0);
      }
    }
  };

  /**
   * Blit the composited bag into any canvas.
   * `bias` is the share of the leftover height that sits ABOVE the bag:
   * 1 bottom-aligns it (size cards, so they compare on a common baseline),
   * ~0.72 balances it while still reading grounded (the hero).
   */
  Customiser.prototype.paintInto = function (canvas, scale, shadow, bias) {
    var ctx = canvas.getContext('2d');
    var W = canvas.width, H = canvas.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, W, H);
    if (!this.layer) return;

    var fit = Math.min(W / this.w, H / this.h) * (scale || 1);
    var dw = this.w * fit, dh = this.h * fit;
    var dx = (W - dw) / 2;
    var dy = (H - dh) * (bias == null ? 1 : bias);

    if (shadow) {
      var cx = W / 2, cy = dy + dh * 0.985, rx = Math.max(dw * 0.40, 1), ry = dh * 0.035;
      var grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, rx);
      grad.addColorStop(0, 'rgba(25,21,18,0.20)');
      grad.addColorStop(1, 'rgba(25,21,18,0)');
      ctx.save();
      ctx.translate(cx, cy); ctx.scale(1, ry / rx); ctx.translate(-cx, -cy);
      ctx.fillStyle = grad;
      ctx.beginPath(); ctx.arc(cx, cy, rx, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
    ctx.drawImage(this.layer, dx, dy, dw, dh);
  };

  /** Flash the zone currently being edited, so it is obvious what changes. */
  Customiser.prototype.paintMarker = function () {
    if (!this.marker || !this.frames.length) return;
    var zone = this.step === 3 ? 'trim' : (this.step === 2 ? 'body' : null);
    this.marker.hidden = !zone;
    if (!zone) return;

    var fr = this.frames[this.frame];
    var g = this.zoneBuf.getContext('2d');
    g.globalCompositeOperation = 'source-over';
    g.clearRect(0, 0, this.w, this.h);
    g.fillStyle = this.root.dataset.accent || '#D03D00';
    g.fillRect(0, 0, this.w, this.h);
    g.globalCompositeOperation = 'destination-in';
    g.drawImage(zone === 'trim' ? fr.trim : fr.body, 0, 0);
    g.globalCompositeOperation = 'source-over';

    var ctx = this.marker.getContext('2d');
    var W = this.marker.width, H = this.marker.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, W, H);
    var fit = Math.min(W / this.w, H / this.h) * (this.size.scale || 1);
    var dw = this.w * fit, dh = this.h * fit;
    ctx.drawImage(this.zoneBuf, (W - dw) / 2, (H - dh) * GROUND_BIAS, dw, dh);

    // Restart the CSS pulse.
    this.marker.classList.remove('is-pulsing');
    void this.marker.offsetWidth;
    this.marker.classList.add('is-pulsing');
  };

  Customiser.prototype.render = function () {
    this.renderLayer();
    if (this.canvas) this.paintInto(this.canvas, this.size.scale, true, GROUND_BIAS);
    this.paintMarker();
    this.renderMinis();
  };

  Customiser.prototype.renderMinis = function () {
    var self = this;
    // Size cards bottom-align on the true ratio, so sizes compare honestly.
    (this.minis || []).forEach(function (m) {
      self.paintInto(m.canvas, m.size.cardScale, false, 1);
    });
    (this.wayCanvases || []).forEach(function (w) {
      // Pairing thumbnails show their own colours, not the current design.
      var b = self.body, t = self.trim;
      self.body = P.byNumber(w.way.body); self.trim = P.byNumber(w.way.trim);
      self.renderLayer();
      self.paintInto(w.canvas, 1, false, 1);
      self.body = b; self.trim = t;
    });
    if (this.wayCanvases && this.wayCanvases.length) this.renderLayer();
  };

  /**
   * `inline` writes explicit CSS pixel dimensions. The main stage wants that.
   * The thumbnails must NOT have it: their width comes from the grid, and a
   * canvas carries an intrinsic width from its backing store — so pinning the
   * style width makes the measurement circular and the cards blow out of
   * their track. They are laid out by CSS (width:100%, height:auto) instead
   * and only their backing store is set here.
   */
  Customiser.prototype.sizeCanvas = function (canvas, cssW, ratio, inline) {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var cssH = cssW * ratio;
    if (inline) {
      canvas.style.width = cssW + 'px';
      canvas.style.height = cssH + 'px';
    }
    canvas.width = Math.max(Math.round(cssW * dpr), 1);
    canvas.height = Math.max(Math.round(cssH * dpr), 1);
  };

  Customiser.prototype.resize = function () {
    if (!this.canvas || !this.w) return;
    var self = this;
    var ratio = this.h / this.w;
    var cssW = Math.max(this.canvas.parentNode.getBoundingClientRect().width, 1);
    this.sizeCanvas(this.canvas, cssW, ratio, true);
    if (this.marker) this.sizeCanvas(this.marker, cssW, ratio, true);

    var thumb = function (c) {
      var w = Math.max(c.parentNode.getBoundingClientRect().width, 1);
      self.sizeCanvas(c, w, ratio, false);
    };
    (this.minis || []).forEach(function (m) { thumb(m.canvas); });
    (this.wayCanvases || []).forEach(function (w) { thumb(w.canvas); });
    this.render();
  };

  // --- UI ------------------------------------------------------------------

  Customiser.prototype.buildUi = function () {
    var self = this;
    this.stepsWrap = this.root.querySelector('[data-sc-steps]');
    this.buildSteps();

    // Tap the bag to turn it: front -> side -> back -> front. This is the thing
    // people try first on a product image, so it beats the old behaviour of
    // jumping to a colour step - the steps are right there on the panel anyway.
    this.root.querySelectorAll('[data-sc-hit]').forEach(function (b) {
      b.setAttribute('aria-label', 'Turn the bag to the next view');
      b.addEventListener('click', function () { self.advanceFrame(); });
    });

    this.sync();
  };

  Customiser.prototype.buildSteps = function () {
    var self = this;
    if (!this.stepsWrap) return;
    this.stepsWrap.innerHTML = '';
    this.steps = [];

    [
      { n: 1, label: 'Size' },
      { n: 2, label: 'Body colour' },
      { n: 3, label: 'Trim colour' },
      { n: 4, label: 'Hardware' }
    ].forEach(function (meta) {
      var wrap = el('div', 'sc__step');
      var head = el('button', 'sc__stephead');
      head.type = 'button';
      head.innerHTML =
        '<span class="sc__stepno"></span>' +
        '<span class="sc__steptext"><span class="sc__steplab">' + meta.label +
        '</span><span class="sc__stepval"></span></span>' +
        '<span class="sc__stepdot" aria-hidden="true"></span>';
      head.addEventListener('click', function () {
        self.setStep(self.step === meta.n ? 0 : meta.n);
      });
      var body = el('div', 'sc__stepbody');
      wrap.appendChild(head);
      wrap.appendChild(body);
      self.stepsWrap.appendChild(wrap);
      self.steps.push({ meta: meta, wrap: wrap, head: head, body: body, built: false });
    });

    this.setStep(this.step);
  };

  Customiser.prototype.setStep = function (n) {
    var self = this;
    this.step = n;
    this.steps.forEach(function (s) {
      var open = s.meta.n === n;
      var done = (s.meta.n === 1 && self.touched.size)
              || (s.meta.n === 2 && self.touched.body)
              || (s.meta.n === 3 && self.touched.trim)
              || (s.meta.n === 4 && self.touched.hardware);
      s.wrap.setAttribute('data-open', open ? 'true' : 'false');
      s.wrap.setAttribute('data-done', (done && !open) ? 'true' : 'false');
      s.head.setAttribute('aria-expanded', open ? 'true' : 'false');
      s.head.querySelector('.sc__stepno').textContent = (done && !open) ? '✓' : s.meta.n;
      s.body.hidden = !open;
      if (open && !s.built) { self.fillStep(s); s.built = true; }
    });
    this.syncSteps();
    // The piping runs down the side seam, so open the trim step on a view
    // where it is actually visible.
    if (n === 3 && this.frames.length > 1 && this.frames[this.frame].key === 'front') {
      var side = this.frames.findIndex(function (f) { return f.key === 'side'; });
      if (side > -1) { this.setFrame(side); return; }
    }
    // The lock and clasp read clearest head-on, so show the front for hardware.
    if (n === 4 && this.frames.length > 1 && this.frames[this.frame].key !== 'front') {
      var front = this.frames.findIndex(function (f) { return f.key === 'front'; });
      if (front > -1) { this.setFrame(front); return; }
    }
    this.render();
  };

  Customiser.prototype.fillStep = function (s) {
    if (s.meta.n === 1) this.fillSizes(s.body);
    if (s.meta.n === 2) this.fillBody(s.body);
    if (s.meta.n === 3) this.fillTrim(s.body);
    if (s.meta.n === 4) this.fillHardware(s.body);
  };

  Customiser.prototype.fillSizes = function (host) {
    var self = this;
    var grid = el('div', 'sc__sizes');
    this.minis = [];
    this.sizeBtns = this.sizes.map(function (sz) {
      var b = el('button', 'sc__size');
      b.type = 'button';
      var stage = el('span', 'sc__sizecanvas');
      var c = document.createElement('canvas');
      stage.appendChild(c);
      b.appendChild(stage);
      b.appendChild(el('span', 'sc__sizename', sz.cm ? sz.cm + ' cm' : sz.label));
      if (sz.dims) b.appendChild(el('span', 'sc__sizedims', sz.dims));
      if (sz.note) b.appendChild(el('span', 'sc__sizenote', sz.note));
      if (sz.price) b.appendChild(el('span', 'sc__sizeprice', sz.price));
      b.addEventListener('click', function () { self.chooseSize(sz); });
      grid.appendChild(b);
      self.minis.push({ canvas: c, size: sz });
      return { btn: b, size: sz };
    });
    host.appendChild(grid);
    this.resize();
  };

  Customiser.prototype.fillBody = function (host) {
    var self = this;

    var toggle = el('button', 'sc__waysbtn', 'Or start from a house pairing');
    toggle.type = 'button';
    var ways = el('div', 'sc__ways');
    ways.hidden = true;
    this.wayCanvases = [];
    P.colourways.forEach(function (way) {
      var b = el('button', 'sc__way');
      b.type = 'button';
      var stage = el('span', 'sc__waycanvas');
      var c = document.createElement('canvas');
      stage.appendChild(c);
      b.appendChild(stage);
      b.appendChild(el('span', 'sc__wayname', way.name));
      b.title = P.byNumber(way.body).name + ' with ' + P.byNumber(way.trim).name;
      b.addEventListener('click', function () { self.applyWay(way); });
      ways.appendChild(b);
      self.wayCanvases.push({ canvas: c, way: way });
    });
    toggle.addEventListener('click', function () {
      self.showWays = !self.showWays;
      ways.hidden = !self.showWays;
      toggle.textContent = self.showWays ? 'Hide house pairings' : 'Or start from a house pairing';
      if (self.showWays) self.resize();
    });

    host.appendChild(toggle);
    host.appendChild(ways);
    host.appendChild(this.buildTray('body'));

    var next = el('button', 'sc__next', 'Next — choose trim');
    next.type = 'button';
    next.addEventListener('click', function () { self.setStep(3); });
    host.appendChild(next);
  };

  Customiser.prototype.fillTrim = function (host) {
    var self = this;

    var sug = el('div', 'sc__suggest');
    sug.appendChild(el('p', 'sc__suglab', ''));
    var row = el('div', 'sc__sugrow');
    row.setAttribute('role', 'group');
    row.setAttribute('aria-label', 'Suggested trim colours');
    sug.appendChild(row);
    host.appendChild(sug);
    this.sugWrap = sug;
    this.sugRow = row;

    host.appendChild(this.buildTray('trim'));

    this.flag = el('p', 'sc__flag',
      'The trim is only a narrow seam, so a shade this close to the body will not '
      + 'be visible on the finished bag. Pick something with more contrast.');
    this.flag.hidden = true;
    host.appendChild(this.flag);

    var next = el('button', 'sc__next', 'Next — choose hardware');
    next.type = 'button';
    next.addEventListener('click', function () { self.setStep(4); });
    host.appendChild(next);

    this.renderSuggestions();
  };

  /** Hardware finish. Its own step so the gold option is actually discoverable. */
  Customiser.prototype.fillHardware = function (host) {
    var self = this;

    var metals = el('div', 'sc__metals');
    metals.setAttribute('role', 'group');
    metals.setAttribute('aria-label', 'Hardware finish');
    this.metalBtns = HARDWARE.map(function (m) {
      var b = el('button', 'sc__metal');
      b.type = 'button';
      b.innerHTML = '<i style="background:' + m.swatch + '"></i>' + m.label;
      b.addEventListener('click', function () { self.setHardware(m); });
      metals.appendChild(b);
      return { btn: b, metal: m };
    });
    host.appendChild(metals);

    host.appendChild(el('p', 'sc__note',
      'The lock, clasp, feet and strap fittings all take the finish you choose here.'));

    this.syncSwatches();
  };

  /** The 40-swatch grid, shared by the body and trim steps. */
  Customiser.prototype.buildTray = function (zone) {
    var self = this;
    var wrap = el('div', 'sc__traywrap');

    var now = el('div', 'sc__now');
    var name = el('span', 'sc__nowname');
    var tools = el('span', 'sc__nowtools');
    var undo = el('button', 'sc__mini', '↩ Undo');
    undo.type = 'button';
    undo.addEventListener('click', function () { self.undo(); });
    var names = el('button', 'sc__mini', this.showNames ? 'Hide names' : 'Show names');
    names.type = 'button';
    names.addEventListener('click', function () {
      self.showNames = !self.showNames;
      self.root.classList.toggle('sc--names', self.showNames);
      names.textContent = self.showNames ? 'Hide names' : 'Show names';
    });
    tools.appendChild(undo); tools.appendChild(names);
    now.appendChild(name); now.appendChild(tools);
    wrap.appendChild(now);

    var tray = el('div', 'sc__tray');
    tray.setAttribute('role', 'radiogroup');
    tray.setAttribute('aria-label', zone === 'trim' ? 'Trim colour' : 'Body colour');
    var swatches = P.trayOrder().map(function (c) {
      var cell = el('span', 'sc__swwrap');
      var b = el('button', 'sc__sw');
      b.type = 'button';
      b.setAttribute('role', 'radio');
      b.style.background = c.display;
      b.title = c.name;
      b.setAttribute('aria-label', c.name);
      b.addEventListener('click', function () { self.setColour(zone, c); });
      b.addEventListener('keydown', function (e) { self.trayKey(e, tray); });
      cell.appendChild(b);
      cell.appendChild(el('span', 'sc__swname', c.name));
      tray.appendChild(cell);
      return { btn: b, colour: c };
    });
    wrap.appendChild(tray);

    var store = { zone: zone, name: name, undo: undo, swatches: swatches };
    if (!this.trays) this.trays = [];
    this.trays.push(store);
    return wrap;
  };

  Customiser.prototype.trayKey = function (e, tray) {
    var cols = columnsOf(tray);
    var btns = Array.prototype.slice.call(tray.querySelectorAll('button'));
    var i = btns.indexOf(document.activeElement);
    if (i < 0) return;
    var next = null;
    if (e.key === 'ArrowRight') next = i + 1;
    else if (e.key === 'ArrowLeft') next = i - 1;
    else if (e.key === 'ArrowDown') next = i + cols;
    else if (e.key === 'ArrowUp') next = i - cols;
    else return;
    e.preventDefault();
    var target = btns[Math.max(0, Math.min(btns.length - 1, next))];
    if (target) target.focus();
  };

  Customiser.prototype.renderSuggestions = function () {
    if (!this.sugRow) return;
    var self = this;
    this.sugWrap.querySelector('.sc__suglab').textContent = 'Goes with ' + this.body.name;
    this.sugRow.innerHTML = '';
    this.sugBtns = P.suggestTrims(this.body.n, 5).map(function (c) {
      var b = el('button', 'sc__sug');
      b.type = 'button';
      b.setAttribute('role', 'radio');
      b.style.background = c.display;
      b.title = c.name;
      b.setAttribute('aria-label', c.name);
      b.addEventListener('click', function () { self.setColour('trim', c); });
      self.sugRow.appendChild(b);
      return { btn: b, colour: c };
    });
    this.syncSwatches();
  };

  Customiser.prototype.buildViews = function () {
    var wrap = this.root.querySelector('[data-sc-views]');
    if (!wrap) return;
    wrap.innerHTML = '';
    if (this.frames.length < 2) return;

    if (this.wantView) {
      for (var i = 0; i < this.frames.length; i++) {
        if (this.frames[i].key === this.wantView) { this.frame = i; break; }
      }
    }
    var self = this;
    this.viewBtns = this.frames.map(function (fr, i) {
      var b = el('button', 'sc__view', fr.label || String(i + 1));
      b.type = 'button';
      b.setAttribute('aria-label', 'Show the ' + (fr.label || 'next') + ' view');
      b.addEventListener('click', function () { self.setFrame(i); });
      wrap.appendChild(b);
      return b;
    });
    this.setFrame(this.frame);
  };

  Customiser.prototype.setFrame = function (i) {
    this.frame = Math.max(0, Math.min(i, this.frames.length - 1));
    var cur = this.frame;
    (this.viewBtns || []).forEach(function (b, n) {
      b.classList.toggle('is-active', n === cur);
      b.setAttribute('aria-pressed', n === cur ? 'true' : 'false');
    });
    this.render();
    this.sync();
  };

  /** Step to the next view, wrapping back round to the front. */
  Customiser.prototype.advanceFrame = function () {
    if (!this.frames || this.frames.length < 2) return;
    // A spin ends in a click event; don't let that count as a tap as well.
    if (this.suppressTap) { this.suppressTap = false; return; }
    this.setFrame((this.frame + 1) % this.frames.length);
  };

  Customiser.prototype.bindDrag = function () {
    var self = this, dragging = false, lastX = 0, acc = 0;
    var stage = this.canvas.parentNode;
    var STEP = 18;

    function down(e) {
      dragging = true; acc = 0;
      self.suppressTap = false;
      lastX = (e.touches ? e.touches[0] : e).clientX;
      stage.classList.add('is-dragging');
    }
    function move(e) {
      if (!dragging) return;
      var x = (e.touches ? e.touches[0] : e).clientX;
      if (Math.abs(x - lastX) > 2) self.suppressTap = true;
      acc += x - lastX; lastX = x;
      var steps = Math.trunc(acc / STEP);
      if (steps) {
        acc -= steps * STEP;
        var n = self.frames.length;
        self.frame = ((self.frame - steps) % n + n) % n;
        self.render();
      }
      if (e.cancelable) e.preventDefault();
    }
    function up() { dragging = false; stage.classList.remove('is-dragging'); }

    stage.addEventListener('mousedown', down);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    stage.addEventListener('touchstart', down, { passive: true });
    stage.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
  };

  // --- state changes -------------------------------------------------------

  Customiser.prototype.push = function () {
    this.history.push({
      body: this.body, trim: this.trim, hardware: this.hardware,
      size: this.size, touched: {
        size: this.touched.size, body: this.touched.body, trim: this.touched.trim
      }
    });
    if (this.history.length > UNDO_DEPTH) this.history.shift();
  };

  Customiser.prototype.undo = function () {
    if (!this.history.length) return;
    var last = this.history.pop();
    this.body = last.body; this.trim = last.trim;
    this.hardware = last.hardware; this.size = last.size;
    this.touched = last.touched;
    this.saved = false;
    this.renderSuggestions();
    this.syncSteps();
    this.sync();
    this.render();
  };

  Customiser.prototype.chooseSize = function (sz) {
    this.push();
    this.size = sz;
    this.touched.size = true;
    this.saved = false;
    this.sync();
    this.setStep(2);
  };

  Customiser.prototype.setColour = function (zone, c) {
    this.push();
    if (zone === 'trim') { this.trim = c; this.touched.trim = true; }
    else { this.body = c; this.touched.body = true; this.renderSuggestions(); }
    this.saved = false;
    this.syncSteps();
    this.sync();
    this.render();
  };

  Customiser.prototype.setHardware = function (m) {
    this.push();
    this.hardware = m;
    this.touched.hardware = true;
    this.saved = false;
    this.syncSteps();
    this.sync();
    this.render();
  };

  Customiser.prototype.applyWay = function (way) {
    this.push();
    this.body = P.byNumber(way.body);
    this.trim = P.byNumber(way.trim);
    this.touched.body = true;
    this.touched.trim = true;
    this.saved = false;
    this.renderSuggestions();
    this.syncSteps();
    this.sync();
    this.render();
  };

  // --- sync ----------------------------------------------------------------

  Customiser.prototype.syncSwatches = function () {
    var self = this;
    (this.trays || []).forEach(function (t) {
      var cur = t.zone === 'trim' ? self.trim : self.body;
      t.name.textContent = cur.name;
      t.undo.disabled = !self.history.length;
      t.swatches.forEach(function (s) {
        var on = s.colour.n === cur.n;
        s.btn.classList.toggle('is-active', on);
        s.btn.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    });
    (this.sugBtns || []).forEach(function (s) {
      var on = s.colour.n === self.trim.n;
      s.btn.classList.toggle('is-active', on);
      s.btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    (this.metalBtns || []).forEach(function (m) {
      var on = m.metal.key === self.hardware.key;
      m.btn.classList.toggle('is-active', on);
      m.btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    (this.sizeBtns || []).forEach(function (s) {
      var on = s.size.code === self.size.code;
      s.btn.classList.toggle('is-active', on);
      s.btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  Customiser.prototype.syncSteps = function () {
    var self = this;
    (this.steps || []).forEach(function (s) {
      var n = s.meta.n;
      var val, dotColour = null;
      if (n === 1) {
        val = self.touched.size ? self.sizeLabel() : 'Choose a size';
      } else if (n === 2) {
        val = self.touched.body ? self.body.name : 'Choose a colour';
        dotColour = self.body.display;
      } else if (n === 3) {
        val = self.touched.trim ? self.trim.name : 'Choose a colour';
        dotColour = self.trim.display;
      } else {
        // Hardware always has a value - it defaults to silver rather than empty.
        val = self.hardware.label;
        dotColour = self.hardware.swatch;
      }
      s.head.querySelector('.sc__stepval').textContent = val;
      var dot = s.head.querySelector('.sc__stepdot');
      if (dotColour === null) { dot.style.display = 'none'; }
      else { dot.style.display = ''; dot.style.background = dotColour; }
      var done = (n === 1 && self.touched.size)
              || (n === 2 && self.touched.body)
              || (n === 3 && self.touched.trim)
              || (n === 4 && self.touched.hardware);
      var open = self.step === n;
      s.wrap.setAttribute('data-done', (done && !open) ? 'true' : 'false');
      s.head.querySelector('.sc__stepno').textContent = (done && !open) ? '✓' : n;
    });
    this.syncSwatches();
  };

  Customiser.prototype.sizeLabel = function () {
    var s = this.size;
    return (s.cm ? s.cm + ' cm' : s.label) + (s.dims ? ' · ' + s.dims : '');
  };

  Customiser.prototype.permalink = function () {
    var u = new URL(window.location.href);
    u.searchParams.set('body', this.body.n);
    u.searchParams.set('trim', this.trim.n);
    u.searchParams.set('size', this.size.code);
    u.searchParams.set('hw', this.hardware.key);
    var fr = this.frames[this.frame];
    if (fr && fr.key) u.searchParams.set('view', fr.key);
    return u.toString();
  };

  /** Short code for the order and the factory, e.g. TT-35-B05T34-S. */
  Customiser.prototype.designCode = function () {
    var sz = this.size.cm ? String(this.size.cm) : String(this.size.label).replace(/\s+/g, '').toUpperCase();
    return 'TT-' + sz
      + '-B' + String(this.body.n).padStart(2, '0')
      + 'T' + String(this.trim.n).padStart(2, '0')
      + '-' + this.hardware.key.charAt(0).toUpperCase();
  };

  /** Push the current design into the product form, the summary and the URL. */
  Customiser.prototype.sync = function () {
    var self = this;
    var wayName = P.matchColourway(this.body.n, this.trim.n) || 'Your design';

    var set = function (sel, v) {
      var n = self.root.querySelector(sel);
      if (n) n.textContent = v;
    };
    set('[data-sc-wayname]', wayName);
    set('[data-sc-capmeta]', this.sizeLabel());

    var sum = this.root.querySelector('[data-sc-summary]');
    if (sum) {
      sum.innerHTML = '';
      [['Size', this.sizeLabel()],
       ['Body', this.body.name],
       ['Trim', this.trim.name],
       ['Hardware', this.hardware.label]].forEach(function (r) {
        var row = el('div', 'sc__sumrow');
        row.appendChild(el('span', null, r[0]));
        row.appendChild(el('b', null, r[1]));
        sum.appendChild(row);
      });
    }

    var live = this.root.querySelector('[data-sc-live]');
    if (live) {
      live.textContent = this.sizeLabel() + ', body ' + this.body.name
        + ', trim ' + this.trim.name + ', ' + this.hardware.label + ' hardware';
    }

    if (this.flag) this.flag.hidden = P.trimReadable(this.body.n, this.trim.n);

    if (this.form) {
      var put = function (sel, v) {
        var n = self.form.querySelector(sel);
        if (n) n.value = v;
      };
      // The SOURCE hex goes on the order, not the on-screen one: the factory
      // matches against the supplier chart, not our display correction.
      put('[data-sc-input="id"]', this.size.variant || '');
      put('[data-sc-input="body"]', this.body.name + ' (' + this.body.source + ')');
      put('[data-sc-input="trim"]', this.trim.name + ' (' + this.trim.source + ')');
      put('[data-sc-input="hardware"]', this.hardware.label);
      put('[data-sc-input="code"]', this.designCode());
      put('[data-sc-input="link"]', this.permalink());

      var btn = this.form.querySelector('[data-sc-submit]');
      if (btn) {
        var ok = !!this.size.variant;
        btn.disabled = !ok;
        btn.textContent = ok
          ? (this.root.dataset.buttonLabel || 'Add to basket') + (this.size.price ? ' — ' + this.size.price : '')
          : 'Unavailable in this size';
      }
    }

    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', this.permalink());
    }
    this.syncSwatches();
  };

  function init() {
    document.querySelectorAll('[data-soleia-customiser]').forEach(function (n) {
      if (!n.__soleia) n.__soleia = new Customiser(n);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
